import axios from 'axios';
import store from '../store';
import tokenRefresh from './tokenRefresh';
import { withCrossTabRefreshLock } from './crossTabRefreshLock';
import { decideUnauthorized, UNAUTHORIZED } from './unauthorizedDecision';
import { getToken } from '@/api/merchy';
import { isStr, isObj, isNum } from '@/utils';
import { apiUrl } from '@/api/merchy';
import apiUrlErrorAlertExeptions from '@/config/apiUrlErrorAlertExeptions';

const isTestMode = process.env.NODE_ENV === 'test';
const maxParallelRequests = import.meta.env.VUE_APP_API_MAX_PARALLEL_REQUESTS;
const postponedRequestsDelay = 500; // ms
// How close to expiry a token has to be for an outgoing request to renew it
// first. Comfortably longer than a request round trip, so a request that passes
// the check does not arrive after the token has died anyway. Must also stay
// well under the backend's token lifetime: set it above that and every request
// looks nearly expired, which costs a refresh per request.
const refreshLeadTime = 30000; // ms
let parallelRequests = 0;
let requestQueue = [];
// For callers that replace the token themselves (the business switch): a 401
// arriving mid-switch must be reported, not answered with a refresh that would
// race the switch. Whether a refresh is running is tokenRefresh's business.
let isRefreshSuspended = false;

export const setRefreshSuspended = (value) => {
  isRefreshSuspended = value;
};

/**
 * @description Resend a request, marked so a second 401 on it is reported
 * instead of looping. Re-entering axios stamps the current token on the way.
 * @param request
 * @returns {Promise}
 */
const retryAfterRefresh = (request) => axios({ ...request, isRetriedAfterRefresh: true });

/**
 * @description Extract the bearer value a request was sent with.
 * @param authHeader
 * @returns {string|null}
 */
const extractBearerToken = (authHeader) =>
  isStr(authHeader) ? authHeader.replace('Bearer ', '') : null;

/**
 * @description Replace request token. Assigns onto the headers object it was
 * handed, so the caller's config is mutated and the spread below is cosmetic.
 * Not worth "fixing" with a spread of `headers` itself: it may be an
 * AxiosHeaders instance rather than a plain object.
 * @param request
 * @returns {*}
 */
const replaceRequestToken = (request) => {
  const { headers } = request;

  const nextToken = getToken();

  headers.Authorization = `Bearer ${nextToken}`;

  return {
    ...request,
    headers,
  };
};

/**
 * @description Handle alerts
 * @param data
 * @param alertType
 */
const handleAlerts = (data, alertType = 'error') =>
  store.dispatch('alerts/set', {
    data,
    alertType,
  });

/**
 * @description Whether the current token is close enough to expiry that an
 * outgoing request should renew it first. False when there is no deadline
 * recorded — a token persisted before `expires_at` existed, or a session that
 * has ended — so the 401 path stays the fallback.
 * @returns {boolean}
 */
const isExpiringSoon = () => {
  const expiresAt = getToken('expires_at');

  return isNum(expiresAt) && expiresAt - Date.now() < refreshLeadTime;
};

/**
 * @description Whether the server gave a definitive answer that the refresh
 * token is no good, as opposed to the request not getting through.
 * @param error
 * @returns {boolean}
 */
const isRejectedByServer = (error) => {
  const status = error?.response?.status;

  return isNum(status) && status >= 400 && status < 500;
};

/**
 * @description Start a refresh cycle, unless one is already running, a caller is
 * replacing the token itself (the business switch), or there is no session to
 * refresh. Guarding here rather than at each call site keeps the two entry
 * points — a 401 that already happened, and `withFreshToken` renewing a token
 * about to expire — from drifting apart.
 *
 * Renews the token, waits out the server pad if this tab really spent the
 * refresh token, then replays whatever was parked.
 */
const startRefresh = () => {
  if (isRefreshSuspended || tokenRefresh.isActive() || !store.getters['auth/isLogged']) {
    return;
  }

  const requestToken = getToken();
  const query = {
    refresh_token: getToken('refresh_token'),
  };

  tokenRefresh
    .run(() =>
      withCrossTabRefreshLock(
        requestToken,
        // Resolves as soon as the new token is in the store, which is all an
        // unsent request waits for.
        () => store.dispatch('auth/refreshToken', query),
        // Another tab won the lock and already rotated the token — adopt it
        // rather than spending the same refresh_token twice.
        (token) => store.dispatch('auth/set', { ...store.state.auth, token }),
      ),
    )
    .then(
      // Give the server a moment before replaying. Only the replayed requests
      // wait: withFreshToken's gate opened when the token landed, so anything
      // unsent has already left.
      () => new Promise((resolve) => setTimeout(resolve, postponedRequestsDelay)),
    )
    .then(() => tokenRefresh.replay(retryAfterRefresh))
    .catch((error) => {
      // Fail the parked callers either way — nothing is coming back for them,
      // and this is also what ends the cycle so a later request can try again.
      tokenRefresh.rejectAll(error);

      // Only the server rejecting the refresh token ends the session. merchy
      // answers an unknown token with 404 and a missing one with 422, so any
      // 4xx is definitive. Everything else — no response at all (offline, DNS,
      // the timeout above) or a 5xx — is transient, and logging the user out
      // over a dropped connection loses their work for no reason. The session
      // stays; the next request retries.
      if (!isRejectedByServer(error)) {
        return handleAlerts({ message: 'Network error. Please try again.' }).catch((err) => err);
      }

      return store.dispatch('auth/redirectInvalidToken').catch((err) => err);
    });
};

/**
 * @description Stamp the request with the current token, holding it first if a
 * refresh is in flight — otherwise it leaves with the expired token and earns
 * its own 401. The refresh call runs on its own axios instance (see
 * api/merchy/auth.js), so it never waits on itself here.
 *
 * A request whose token is nearly dead starts the refresh itself and then waits
 * on the same gate, so it goes out renewed and never spends a round trip on a
 * 401 nobody needed. There is no timer behind this: a refresh only happens
 * because a request was going out anyway, which means an idle tab never renews
 * and a backgrounded one never wakes up to do it. That holds only while nothing
 * polls — no setInterval, socket or beacon goes through this instance today.
 * @param config
 * @returns {Promise|object}
 */
const withFreshToken = (config) => {
  if (isExpiringSoon()) {
    startRefresh();
  }

  const refreshing = tokenRefresh.getPending();

  return refreshing ? refreshing.then(() => replaceRequestToken(config)) : replaceRequestToken(config);
};

/**
 * @description Add to request queue
 * @param config
 * @param resolve
 */
const addToRequestQueue = (config, resolve) => {
  requestQueue.push(() => resolve(withFreshToken(config)));
};

/**
 * @description Call postponed requests
 */
const callRequestQueue = () => {
  if (requestQueue.length > 0) {
    const [firstInQueue, ...remainingRequests] = requestQueue;
    firstInQueue();
    requestQueue = remainingRequests;
  }

  if (parallelRequests > 0) {
    parallelRequests -= 1;
  }
};

/**
 * @description Headers
 * @param key
 * @param value
 * @param valueKey
 */
const headers = (key, value, valueKey) => {
  if (!value || (typeof valueKey !== 'undefined' && !value[valueKey])) {
    return;
  }

  let header = value;

  if (valueKey) {
    header = value[valueKey];
  }

  axios.defaults.headers.common[key] = header;
};

/**
 * @description Set CSRF token
 * @param resHeaders
 */
export const setCsrfToken = (resHeaders) => {
  const csrfToken = resHeaders['x-csrf-token'];

  if (isStr(csrfToken)) {
    headers('X-CSRF-Token', csrfToken);
  }
};

/**
 * @description Attach CSRF token
 * @param config
 * @param csrfToken
 * @returns {FormData|string}
 */
const attachCsrfToken = (config, csrfToken) => {
  let requestData = config.data;

  if (isStr(requestData)) {
    // json
    const parsedData = JSON.parse(config.data);
    parsedData['X-CSRF-Token'] = csrfToken;
    requestData = JSON.stringify(parsedData);
  } else if (requestData instanceof FormData) {
    // form data
    requestData.append('X-CSRF-Token', csrfToken);
  } else if (isObj(requestData)) {
    // object
    requestData['X-CSRF-Token'] = csrfToken;
  }

  return requestData;
};

/**
 * @description Request interceptor
 * @param config
 * @returns {*}
 */
const requestInterceptor = (config) => {
  const augmentedConfig = { ...config };

  if (config.method !== 'get') {
    const csrfToken = axios.defaults.headers.common['X-CSRF-Token'];
    augmentedConfig.data = attachCsrfToken(config, csrfToken);
  }

  if (parallelRequests < maxParallelRequests) {
    parallelRequests += 1;
    return withFreshToken(augmentedConfig);
  }

  // add request to request queue. The token is stamped on release, not on
  // queueing, so a request that waited through a refresh leaves with the new one.
  // eslint-disable-next-line
  return new Promise((resolve) => addToRequestQueue(augmentedConfig, resolve));
};

/**
 * @description Response success handler
 * @param res
 * @returns {*}
 */
const responseSuccessHandler = (res) => {
  // TODO: Errors should not fall through the axios global error handler to the success handler. Investigate and implement a better solution
  // TODO: Maybe we need to send a bugsnag report here?
  // handle errors that leak through
  if (res.data.status === 'error') {
    return responseErrorHandler({
      response: res,
      config: res.config,
    });
  }

  callRequestQueue();
  setCsrfToken(res.headers);

  return res;
};

/**
 * @description Standardize error obj
 * @param res
 * @returns {{statusText}|*}
 */
const standardizeErrorObj = (res) => {
  res.data.message = res.data.message || res.statusText;
  res.data.data = res.data.data || {};
  res.data.data.message = res.data.data.message || res.data.message;

  return res;
};

/**
 * @description Is unauthenticated error
 * @param res
 * @returns {boolean}
 */
// TODO: Consider using a more strict evaluation, not relying on string comparison
const isUnauthenticatedErr = (res) =>
  res.status === 401 &&
  (res.data.data.message === 'Unauthenticated.' ||
    res.data.data.message === 'Unauthorized');

/**
 * The download endpoints answer errors as JSON even when the request asked for a blob.
 */
const blobErrorMessage = async (body) => {
  const reportTooBig =
    'The report you are trying to download is too big. Please use the filtering option and select a shorter time range.';

  if (!(body instanceof Blob)) {
    return reportTooBig;
  }

  try {
    const parsed = JSON.parse(await body.text());

    return parsed?.data?.message || parsed?.message || reportTooBig;
  } catch (e) {
    return reportTooBig;
  }
};

/**
 * @description Response error handler
 * @param err
 * @returns {*}
 */
const responseErrorHandler = async (err) => {
  callRequestQueue();

  // handle generic error
  if (!isObj(err.response) || !isObj(err.response.data)) {
    handleAlerts({
        message: 'Network error. Please try again.',
    }).catch((err) => err);

    return Promise.reject(err);
  }

  // standardize response
  const res = standardizeErrorObj(err.response);

  if (isTestMode) {
    return Promise.reject(err);
  }

  // A blob error body is not parsed by axios, so the message here would otherwise be
  // "Internal Server Error". Read it: an invoice the ERP has not produced yet says so, and
  // only when the server sent nothing usable does the report hint this was written for apply.
  if (res.request.responseType === 'blob') {
    const errorMessage = await blobErrorMessage(err.response.data);

    res.data.message = errorMessage;
    res.data.data.message = errorMessage;
  }


  // apiUrl is usually the base url(ex. https://api.example.com) + 'api/'
  const reqUrl = err.request.responseURL.replace(apiUrl, '');
  // if the request url is in the exception list, stop here and do not show error alert
  const index = apiUrlErrorAlertExeptions.findIndex(ex =>
    ex.url === reqUrl && ex.method === err.config.method && ex.code === res.status
  );
  if (index !== -1) {
    return Promise.reject(err);
  }


  // handle authenticated/authorized/public error
  if (!isUnauthenticatedErr(res)) {
    handleAlerts({
      data: res.data,
      params: res.config.params || res.config.data,
    }).catch((err) => err);
    return Promise.reject(err);
  }

  const action = decideUnauthorized({
    isRetry: Boolean(err.config.isRetriedAfterRefresh),
    usedToken: extractBearerToken(err.config.headers.Authorization),
    currentToken: getToken(),
  });

  if (action === UNAUTHORIZED.REPORT) {
    return Promise.reject(err);
  }

  if (action === UNAUTHORIZED.RETRY) {
    return retryAfterRefresh(err.config);
  }

  // handle unauthenticated/unauthorized error
  startRefresh();

  // Park only when a cycle will actually replay or reject this later — the call
  // above is a no-op during a business switch, and a parked caller with nothing
  // coming waits forever. isActive(), not getPending(): a 401 arriving during
  // the pad still has a replay coming.
  if (!tokenRefresh.isActive()) {
    return Promise.reject(err);
  }

  // postpone all requests while refreshing token
  return new Promise((resolve) => tokenRefresh.postpone(err.config, resolve));
};

// use custom interceptor
axios.interceptors.request.use(requestInterceptor);

// use custom response handlers
axios.interceptors.response.use(responseSuccessHandler, responseErrorHandler);
